import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "selectAll", "banner", "bannerCount",
                    "bannerPartial", "bannerAllInFilter",
                    "bannerError",
                    "popup", "popupHeading", "popupItems",
                    "confirmDialog", "confirmTitle", "confirmMessage", "confirmLabel",
                    "verdictDialog", "verdictTitle", "verdictMessage",
                    "verdictReasonStep", "verdictReason",
                    "verdictApprove", "verdictApproveLabel", "verdictReject", "verdictRejectLabel"]
  static values = {
    total: Number,
    pageSize: Number,
    noun: { type: String, default: "" },
    actionLabels: { type: Object, default: {} },
    actionOrder: { type: Array, default: [] },
    actionEndpoints: { type: Object, default: {} },
    actionConfirms: { type: Object, default: {} },
    actionConfirmTitles: { type: Object, default: {} },
    actionConfirmLabels: { type: Object, default: {} },
    actionSuppressions: { type: Object, default: {} },
    hiddenActions: { type: Array, default: [] },
    actionVerdicts: { type: Object, default: {} },
    singleOnlyActions: { type: Array, default: [] },
    linkActions: { type: Array, default: [] },
    actionSubmenus: { type: Object, default: {} },
    emptyHeading: { type: String, default: "" },
    emptySelectionHeading: { type: String, default: "" },
    tooManyRowsHeading: { type: String, default: "" },
    summaryUrl: { type: String, default: "" },
    bannerAllInFilterTemplate: { type: String, default: "" }
  }

  connect() {
    this.allInFilter = false
    this.filterSummary = null
    this.summaryGeneration = 0
    this.selectedRowIds = new Set()
    this.onDocClick = this.onDocClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener("click", this.onDocClick)
    document.addEventListener("keydown", this.onKeydown)
    this.updateState()
    this.onMorph = () => this.reapplyAfterMorph()
    document.addEventListener("turbo:morph", this.onMorph)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    document.removeEventListener("keydown", this.onKeydown)
    if (this.onMorph) document.removeEventListener("turbo:morph", this.onMorph)
  }

  reapplyAfterMorph() {
    this.restoreCheckedProperties()
    this.updateState()
  }

  restoreCheckedProperties() {
    this.checkboxTargets.forEach(cb => { cb.checked = this.wasSelected(cb) })
  }

  wasSelected(checkbox) {
    if (this.allInFilter) return true
    return this.selectedRowIds.has(this.rowIdOf(checkbox))
  }

  rowIdOf(checkbox) {
    return checkbox.dataset.id || checkbox.closest("tr")?.dataset.rowId
  }

  rememberSelection() {
    this.selectedRowIds = new Set(
      this.checkboxTargets.filter(cb => cb.checked).map(cb => this.rowIdOf(cb)).filter(Boolean)
    )
  }

  toggleRow() {
    this.clearFilterSelection()
    this.updateState()
  }

  toggleAll(event) {
    const checked = event.currentTarget.checked
    this.checkboxTargets.forEach(cb => { cb.checked = checked })
    this.clearFilterSelection()
    this.updateState()
  }

  async selectAllInFilter(event) {
    event.preventDefault()
    if (!this.hasSummaryUrlValue || this.summaryFetchInFlight) return

    const link = event.currentTarget
    const generation = ++this.summaryGeneration
    this.setSummaryFetchBusy(link, true)
    try {
      const url = `${this.summaryUrlValue}?${this.filterParams()}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error(`selection summary responded ${response.status}`)
      const summary = await response.json()
      if (generation !== this.summaryGeneration) return
      this.filterSummary = summary
      this.allInFilter = true
      this.checkboxTargets.forEach(cb => { cb.checked = true })
      this.hidePopup()
      this.closeConfirm()
      this.showSummaryError(false)
      this.updateState()
    } catch (_e) {
      if (generation === this.summaryGeneration) this.showSummaryError(true)
    } finally {
      this.setSummaryFetchBusy(link, false)
    }
  }

  setSummaryFetchBusy(link, busy) {
    this.summaryFetchInFlight = busy
    if (!link) return
    link.setAttribute("aria-busy", busy ? "true" : "false")
    link.classList.toggle("pointer-events-none", busy)
    link.classList.toggle("opacity-60", busy)
  }

  showSummaryError(show) {
    if (this.hasBannerErrorTarget) this.bannerErrorTarget.hidden = !show
  }

  clearFilterSelection() {
    this.allInFilter = false
    this.filterSummary = null
    this.summaryGeneration += 1
    this.showSummaryError(false)
  }

  filterParams() {
    const params = new URLSearchParams(window.location.search)
    params.delete("page")
    return params
  }

  filterCount() {
    return this.filterSummary ? this.filterSummary.count : this.totalValue
  }

  exceedsLimit() {
    return Boolean(this.menuWholeFilter && this.filterSummary && this.filterSummary.exceeds_limit)
  }

  clearSelection(event) {
    event.preventDefault()
    this.checkboxTargets.forEach(cb => { cb.checked = false })
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.clearFilterSelection()
    this.updateState()
  }

  selectedCount() {
    return this.checkboxTargets.filter(cb => cb.checked).length
  }

  selectedRows() {
    return this.checkboxTargets.filter(cb => cb.checked).map(cb => cb.closest("tr")).filter(Boolean)
  }

  updateState() {
    this.rememberSelection()

    const selected = this.selectedCount()
    const rows = this.checkboxTargets.length
    const allOnPageSelected = rows > 0 && this.checkboxTargets.every(cb => cb.checked)

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = allOnPageSelected
      this.selectAllTarget.indeterminate = !allOnPageSelected && selected > 0
    }

    if (!this.hasBannerTarget) return

    if (selected === 0) {
      this.banner(false)
      return
    }

    this.banner(true)
    if (this.hasBannerCountTarget) {
      this.bannerCountTarget.textContent = this.formatNumber(selected)
    }
    if (this.hasBannerPartialTarget) this.bannerPartialTarget.hidden = this.allInFilter
    if (this.hasBannerAllInFilterTarget) {
      this.bannerAllInFilterTarget.hidden = !this.allInFilter
      if (this.allInFilter) {
        this.bannerAllInFilterTarget.textContent =
          this.bannerAllInFilterTemplateValue.replace("{count}", this.formatNumber(this.filterCount()))
      }
    }
  }

  banner(show) {
    if (show) this.bannerTarget.removeAttribute("hidden")
    else this.bannerTarget.setAttribute("hidden", "hidden")
  }

  formatNumber(n) {
    return Number(n).toLocaleString()
  }

  showPopup(event) {
    if (!this.hasPopupTarget) return
    event.preventDefault()

    let targetRows = this.selectedRows()
    const wholeFilter = this.allInFilter && targetRows.length > 0
    if (targetRows.length === 0) targetRows = [event.currentTarget]

    const count = wholeFilter ? this.filterCount() : targetRows.length
    const actions = this.suppress(
      wholeFilter
        ? this.singleOnlyFiltered(this.filterSummary.actions, Math.max(count, 2))
        : this.intersectActions(targetRows, count)
    )

    this.menuRows = targetRows
    this.menuActions = actions
    this.menuCount = count
    this.menuWholeFilter = wholeFilter
    this.renderPopupItems(actions)
    this.setPopupHeading(count, actions.length)

    const popup = this.popupTarget
    popup.classList.remove("hidden")
    const margin = 4
    const x = Math.min(event.clientX, window.innerWidth - popup.offsetWidth - margin)
    const y = Math.min(event.clientY, window.innerHeight - popup.offsetHeight - margin)
    popup.style.left = `${x}px`
    popup.style.top = `${y}px`
  }

  hidePopup() {
    if (this.hasPopupTarget) this.popupTarget.classList.add("hidden")
  }

  popupOpen() {
    return this.hasPopupTarget && !this.popupTarget.classList.contains("hidden")
  }

  onDocClick(event) {
    if (this.popupOpen() && !this.popupTarget.contains(event.target)) this.hidePopup()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.hidePopup()
  }

  intersectActions(rows, count) {
    if (rows.length === 0) return []
    const sets = rows.map(r => new Set(this.rowActions(r)))
    return this.singleOnlyFiltered(
      this.actionOrderValue.filter(key => sets.every(s => s.has(key))),
      count
    )
  }

  singleOnlyFiltered(keys, count) {
    if (count <= 1) return this.actionOrderValue.filter(key => keys.includes(key))
    const singleOnly = new Set(this.singleOnlyActionsValue)
    return this.actionOrderValue.filter(key => keys.includes(key) && !singleOnly.has(key))
  }

  suppress(actions) {
    const suppressed = new Set()
    actions.forEach(key => (this.actionSuppressionsValue[key] || []).forEach(k => suppressed.add(k)))
    const hidden = new Set(this.hiddenActionsValue)
    return actions.filter(key => !suppressed.has(key) && !hidden.has(key))
  }

  rowActions(row) {
    return (row.dataset.actions || "").split(",").map(s => s.trim()).filter(Boolean)
  }

  renderPopupItems(actions) {
    const container = this.popupItemsTarget
    container.innerHTML = ""
    container.classList.toggle("hidden", actions.length === 0)
    if (actions.length === 0) return

    for (const key of actions) {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"

      const iconSlot = document.createElement("span")
      iconSlot.className = "inline-flex items-center justify-center w-4 h-4 shrink-0 text-zinc-500 dark:text-zinc-400"
      const tpl = this.element.querySelector(`template[data-action-icon="${key}"]`)
      if (tpl) iconSlot.appendChild(tpl.content.cloneNode(true))

      const labelSpan = document.createElement("span")
      labelSpan.textContent = this.actionLabelsValue[key] || key

      btn.appendChild(iconSlot)
      btn.appendChild(labelSpan)
      const submenu = this.actionSubmenusValue[key]
      const confirmMsg = this.actionConfirmsValue[key]
      const verdict = this.actionVerdictsValue[key]
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        if (this.linkActionsValue.includes(key)) this.openLink(key, this.menuRows)
        else if (submenu) this.openSubmenu(key, submenu)
        else if (verdict) this.openVerdict(verdict)
        else if (confirmMsg) this.openConfirm(key, confirmMsg)
        else this.submitAction(key)
      })
      container.appendChild(btn)
    }
  }

  openConfirm(key, message) {
    this.pendingKey = key
    this.hidePopup()
    this.confirmMessageTarget.textContent = message.replace("{count}", this.formatNumber(this.menuCount))
    if (this.hasConfirmTitleTarget) {
      this.confirmTitleTarget.textContent = this.actionConfirmTitlesValue[key] ?? ""
    }
    if (this.hasConfirmLabelTarget) {
      this.confirmLabelTarget.textContent = this.actionConfirmLabelsValue[key] ?? ""
    }
    this.confirmDialogTarget.hidden = false
  }

  confirmAction() {
    const key = this.pendingKey
    this.closeConfirm()
    if (key) this.submitAction(key)
  }

  closeConfirm() {
    if (this.hasConfirmDialogTarget) this.confirmDialogTarget.hidden = true
    this.pendingKey = null
  }

  closeConfirmOnBackdrop(event) {
    if (event.target === event.currentTarget) this.closeConfirm()
  }

  openVerdict(cfg) {
    this.pendingVerdict = cfg
    this.hidePopup()
    this.showVerdictStepOne()
    this.verdictDialogTarget.hidden = false
  }

  showVerdictStepOne() {
    const cfg = this.pendingVerdict
    const count = this.formatNumber(this.menuCount)
    this.verdictTitleTarget.textContent = cfg.title
    this.verdictMessageTarget.textContent = (cfg.message || "").replace("{count}", count)
    this.verdictReasonStepTarget.hidden = true
    this.verdictReasonTarget.value = ""
    this.verdictApproveTarget.classList.remove("hidden")
    this.verdictApproveLabelTarget.textContent = cfg.approve_label
    this.verdictRejectLabelTarget.textContent = cfg.reject_label
    this.verdictRejectTarget.disabled = false
    this.reasonStep = false
  }

  rejectVerdict() {
    if (!this.reasonStep) {
      const cfg = this.pendingVerdict
      this.verdictTitleTarget.textContent = cfg.reason_title
      this.verdictMessageTarget.textContent = cfg.reason_message
      this.verdictRejectLabelTarget.textContent = cfg.reason_submit
      this.verdictReasonStepTarget.hidden = false
      this.verdictApproveTarget.classList.add("hidden")
      this.reasonStep = true
      this.validateVerdictReason()
      this.verdictReasonTarget.focus()
      return
    }

    const reason = this.verdictReasonTarget.value.trim()
    if (reason === "") return
    const cfg = this.pendingVerdict
    this.closeVerdict()
    this.submitTo(cfg.reject_endpoint, { reason: reason })
  }

  validateVerdictReason() {
    this.verdictRejectTarget.disabled = this.verdictReasonTarget.value.trim() === ""
  }

  approveVerdict() {
    const cfg = this.pendingVerdict
    this.closeVerdict()
    this.submitTo(cfg.approve_endpoint)
  }

  cancelVerdict() {
    if (this.reasonStep) this.showVerdictStepOne()
    else this.closeVerdict()
  }

  closeVerdict() {
    if (this.hasVerdictDialogTarget) this.verdictDialogTarget.hidden = true
    if (this.hasVerdictReasonTarget) this.verdictReasonTarget.value = ""
    this.pendingVerdict = null
    this.reasonStep = false
  }

  closeVerdictOnBackdrop(event) {
    if (event.target === event.currentTarget) this.closeVerdict()
  }

  openSubmenu(key, cfg) {
    if (this.hasPopupHeadingTarget) this.popupHeadingTarget.classList.add("hidden")
    const container = this.popupItemsTarget
    container.classList.remove("hidden")
    container.innerHTML = ""
    container.appendChild(this.submenuHeader(cfg))
    cfg.options.forEach(opt => {
      container.appendChild(this.submenuButton(opt.label, () => {
        this.submitAction(key, { duration: opt.key })
      }))
    })
    container.appendChild(this.submenuButton(cfg.custom, () => this.renderSubmenuCustom(key, cfg), true))
  }

  renderSubmenuCustom(key, cfg) {
    const container = this.popupItemsTarget
    container.innerHTML = ""
    container.appendChild(this.submenuHeader(cfg))

    const wrap = document.createElement("div")
    wrap.className = "flex flex-col gap-2 px-3 py-2"
    const input = document.createElement("input")
    input.type = "date"
    input.className = "rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
    const confirm = document.createElement("button")
    confirm.type = "button"
    confirm.className = "rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
    confirm.textContent = cfg.confirm
    confirm.addEventListener("click", (e) => {
      e.stopPropagation()
      if (!input.value) return
      this.submitAction(key, { until: input.value })
    })
    wrap.appendChild(input)
    wrap.appendChild(confirm)
    container.appendChild(wrap)
  }

  submenuHeader(cfg) {
    const header = document.createElement("div")
    header.className = "flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800"
    const back = document.createElement("button")
    back.type = "button"
    back.className = "inline-flex items-center justify-center w-4 h-4 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
    back.setAttribute("aria-label", cfg.back)
    const tpl = this.element.querySelector('template[data-action-icon="arrow_left"]')
    if (tpl) back.appendChild(tpl.content.cloneNode(true))
    else back.textContent = "‹"
    back.addEventListener("click", (e) => {
      e.stopPropagation()
      this.renderPopupItems(this.menuActions)
      this.setPopupHeading(this.menuCount, this.menuActions.length)
    })
    const label = document.createElement("span")
    label.textContent = cfg.header
    header.appendChild(back)
    header.appendChild(label)
    return header
  }

  submenuButton(text, onClick, separated = false) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "flex items-center w-full text-left px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
    if (separated) btn.className += " border-t border-zinc-100 dark:border-zinc-800"
    btn.textContent = text
    btn.addEventListener("click", (e) => { e.stopPropagation(); onClick() })
    return btn
  }

  setPopupHeading(count, actionCount) {
    const heading = this.popupHeadingTarget
    const empty = actionCount === 0
    let text = ""
    if (count === 1) {
      if (empty) text = this.emptyHeadingValue
    } else if (empty && this.exceedsLimit()) {
      text = this.tooManyRowsHeadingValue
    } else {
      text = empty ? this.emptySelectionHeadingValue : `${this.formatNumber(count)} ${this.nounValue || "items"}`
    }

    const borderClasses = ["border-t", "border-zinc-100", "dark:border-zinc-800"]
    const errorClasses = ["text-rose-600", "dark:text-rose-400"]
    const normalClasses = ["text-zinc-500", "dark:text-zinc-400", "uppercase", "tracking-wide"]

    if (text === "") {
      heading.classList.add("hidden")
      heading.textContent = ""
      this.popupItemsTarget.classList.remove(...borderClasses)
      return
    }
    heading.classList.remove("hidden")
    heading.textContent = text
    this.popupItemsTarget.classList.add(...borderClasses)
    heading.classList.remove(...(empty ? normalClasses : errorClasses))
    heading.classList.add(...(empty ? errorClasses : normalClasses))
  }

  openLink(key, targetRows) {
    this.hidePopup()
    const row = targetRows[0]
    if (!row) return
    let urls = {}
    try { urls = JSON.parse(row.dataset.actionUrls || "{}") } catch (_e) { return }
    const url = urls[key]
    if (url) window.open(url, "_blank", "noopener")
  }

  submitAction(key, extra = {}) {
    this.hidePopup()
    this.submitTo(this.actionEndpointsValue[key], extra)
  }

  submitTo(url, extra = {}) {
    const ids = this.menuWholeFilter ? [] : this.menuRows.map(r => r.dataset.rowId).filter(Boolean)
    if (!url || (!this.menuWholeFilter && ids.length === 0)) return

    const form = document.createElement("form")
    form.method = "post"
    form.action = url
    form.style.display = "none"

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) form.appendChild(this.hidden("authenticity_token", token))
    if (this.menuWholeFilter) {
      form.appendChild(this.hidden("all_in_filter", "1"))
      this.filterParams().forEach((value, name) => form.appendChild(this.hidden(name, value)))
    } else {
      ids.forEach(id => form.appendChild(this.hidden("ids[]", id)))
    }
    Object.entries(extra).forEach(([name, value]) => form.appendChild(this.hidden(name, value)))

    document.body.appendChild(form)
    if (form.requestSubmit) form.requestSubmit()
    else form.submit()
  }

  hidden(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }
}
